// Lib
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { CloseDrawer, RemoveDrawerState } from '@actions/drawer';

// Types
import { RootState } from '@app-types/index';

export interface UseDrawerReturn {
  display: boolean;
  children: React.ReactNode;
  style: React.CSSProperties | undefined;
}

const useDrawer = (): UseDrawerReturn => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { display, children, style } = useSelector(
    (state: RootState) => state.Drawer,
  );

  // Close on route change. Children are dropped by the effect below, after the
  // slide-out animation finishes — never synchronously, or the content
  // disappears mid-animation.
  useEffect(() => {
    if (display) dispatch(CloseDrawer());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Once closed, drop the stale children — but wait longer than the 0.3s
  // slide-out transition (Drawer.styles) so they outlive the animation instead
  // of racing it (which made the panel snap out abruptly).
  useEffect(() => {
    if (display) return undefined;
    const timerId = setTimeout(() => {
      dispatch(RemoveDrawerState());
    }, 350);
    return () => clearTimeout(timerId);
  }, [dispatch, display]);

  return {
    display,
    children,
    style,
  };
};

export default useDrawer;
